/* ------------------------------------------------------------------ *
 * Listing sharing helpers — public link building + social share URLs.
 * ------------------------------------------------------------------ */

export const PUBLIC_SITE_URL = "https://map-home-hub.lovable.app";

export const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=70";

/** Canonical public path for a listing (works for guests, no login needed). */
export function publicListingPath(id: string) {
  return `/l/${id}`;
}

/** Absolute, shareable URL for a listing. */
export function publicListingUrl(id: string) {
  return `${PUBLIC_SITE_URL}${publicListingPath(id)}`;
}

export function listingSeoTitle(title: string, priceLabel?: string | null, city?: string | null) {
  const parts = [title.trim()];
  if (priceLabel) parts.push(priceLabel);
  if (city) parts.push(city);
  const base = parts.filter(Boolean).join(" · ");
  const full = `${base} | Landoo`;
  return full.length > 65 ? `${title.trim().slice(0, 50)} | Landoo` : full;
}

export function listingSeoDescription(input: {
  title: string;
  priceLabel?: string | null;
  propertyType?: string | null;
  city?: string | null;
  address?: string | null;
  description?: string | null;
}) {
  const lead = [
    input.propertyType,
    input.priceLabel ? `at ${input.priceLabel}` : null,
    (input.city ?? input.address) ? `in ${input.city ?? input.address}` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const tail = (input.description ?? "").replace(/\s+/g, " ").trim();
  const text = [lead, tail].filter(Boolean).join(". ");
  const out = text || `${input.title} on Landoo — photos, location map and partner contact.`;
  return out.length > 155 ? `${out.slice(0, 152)}…` : out;
}

export type ShareChannel = "whatsapp" | "facebook" | "x" | "telegram";

export interface ShareTarget {
  key: ShareChannel;
  label: string;
  href: string;
}

export function buildShareTargets(url: string, text: string): ShareTarget[] {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  return [
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${t}%20${u}` },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    },
    { key: "x", label: "X", href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
    { key: "telegram", label: "Telegram", href: `https://t.me/share/url?url=${u}&text=${t}` },
  ];
}

/** Copy text to the clipboard with a legacy fallback for older browsers. */
export async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    if (typeof document === "undefined") return false;
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}
